import { useCallback, useEffect, useState } from "react";
import { RefreshCw, Save, ShieldCheck } from "lucide-react";
import type { AccountStatus } from "@/types";

interface UserRow {
  id: number;
  email: string;
  account_status: AccountStatus;
  created_at: string;
}

const STATUS_OPTIONS: { value: AccountStatus; label: string; color: string; bg: string }[] = [
  { value: "approved", label: "Aprovado", color: "var(--cb-success)", bg: "var(--cb-success-dim)" },
  { value: "pending", label: "Pendente", color: "var(--cb-warning)", bg: "var(--cb-warning-dim)" },
  { value: "suspended", label: "Suspenso", color: "var(--cb-error)", bg: "var(--cb-error-dim)" },
];

function statusColor(status: AccountStatus): string {
  if (status === "approved") return "var(--cb-success)";
  if (status === "suspended") return "var(--cb-error)";
  return "var(--cb-warning)";
}

export function AdminTab({ onChanged }: { onChanged: () => void }) {
  const [users, setUsers] = useState<UserRow[]>([]);
  const [draft, setDraft] = useState<Record<number, AccountStatus>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [savingId, setSavingId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setError("");
    setLoading(true);
    try {
      const res = await fetch("/api/admin/users");
      const data = (await res.json()) as { users?: UserRow[]; error?: string };
      if (!res.ok) {
        setError(data.error ?? "Não foi possível carregar os usuários");
        return;
      }
      setUsers(data.users ?? []);
      setDraft({});
    } catch {
      setError("Erro de rede");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async (id: number) => {
    const next = draft[id];
    if (!next) return;
    setSavingId(id);
    try {
      const res = await fetch(`/api/admin/users/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ account_status: next }),
      });
      const data = (await res.json()) as { error?: string };
      if (!res.ok) {
        setError(data.error ?? "Falha ao salvar");
        return;
      }
      await load();
      onChanged();
    } catch {
      setError("Erro de rede");
    } finally {
      setSavingId(null);
    }
  };

  return (
    <>
      <section className="cb-panel cb-rise">
        <div className="cb-panel__head">
          <span className="cb-panel__title">
            <ShieldCheck size={13} />
            Usuários
          </span>
          <button type="button" className="cb-btn cb-btn--ghost cb-btn--sm" disabled={loading} onClick={() => void load()}>
            <RefreshCw size={12} className={loading ? "animate-spin" : undefined} />
            Atualizar
          </button>
        </div>
        <div className="cb-panel__body" style={{ display: "grid", gap: "0.9rem" }}>
          <p style={{ margin: 0, color: "var(--cb-muted)", fontSize: "0.85rem" }}>
            Contas novas entram como <span className="cb-num" style={{ color: "var(--cb-warning)" }}>pendente</span> e só
            podem usar o painel depois de <span className="cb-num" style={{ color: "var(--cb-success)" }}>aprovada</span>.
          </p>
          {error && (
            <p className="cb-fade" style={{ margin: 0, fontFamily: "var(--font-mono)", fontSize: "0.75rem", color: "var(--cb-error)" }}>
              {error}
            </p>
          )}
          {loading && users.length === 0 && <div className="cb-empty">carregando usuários…</div>}
          {!loading && users.length === 0 && !error && <div className="cb-empty">nenhum usuário registrado</div>}
        </div>
      </section>

      {users.length > 0 && (
        <section className="cb-panel cb-rise-1">
          {users.map((u) => {
            const current = draft[u.id] ?? u.account_status;
            const dirty = draft[u.id] !== undefined && draft[u.id] !== u.account_status;
            const isSaving = savingId === u.id;
            return (
              <div className="cb-class" key={u.id} style={{ gridTemplateColumns: "minmax(0, 1fr) auto" }}>
                <div style={{ minWidth: 0, display: "grid", gap: "0.5rem" }}>
                  <div className="cb-row" style={{ gap: "0.5rem" }}>
                    <span className="cb-led" style={{ color: statusColor(u.account_status) }} />
                    <span className="cb-num" style={{ fontSize: "0.85rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {u.email}
                    </span>
                    <span className="cb-label" style={{ letterSpacing: "0.1em" }}>
                      desde {new Date(u.created_at).toLocaleDateString("pt-BR")}
                    </span>
                  </div>
                  <div className="cb-row" style={{ gap: "0.3rem" }}>
                    {STATUS_OPTIONS.map((opt) => {
                      const active = current === opt.value;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          className="cb-btn cb-btn--sm"
                          onClick={() => setDraft((d) => ({ ...d, [u.id]: opt.value }))}
                          style={
                            active
                              ? { borderColor: opt.color, background: opt.bg, color: opt.color }
                              : undefined
                          }
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                {dirty && (
                  <button type="button" className="cb-btn cb-btn--primary cb-btn--sm" disabled={isSaving} onClick={() => void save(u.id)}>
                    <Save size={12} />
                    {isSaving ? "Salvando…" : "Salvar"}
                  </button>
                )}
              </div>
            );
          })}
        </section>
      )}
    </>
  );
}
